import { useState } from 'react';
import { Upload, CheckCircle, Loader2, AlertCircle, Camera, FolderOpen, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';
import { logErrorToBackend } from '../utils/errorLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegistroVIP() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expedienteNumero, setExpedienteNumero] = useState('');
  const [exitoso, setExitoso] = useState(false);

  // VERSION: 2.0 - Backend handles all uploads
  console.log('📌 RegistroVIP v2.0 - Backend-managed uploads');

  // Form data
  const [formData, setFormData] = useState({
    nombre_completo: '',
    cedula: '',
    email: '',
    telefono: '',
    direccion: '',
    fecha_inscripcion: '',
    monto_acumulado: '',
    plan_adquirido: '',
    fecha_ultimo_pago: '',
    motivo_retiro: ''
  });

  // Archivos
  const [archivos, setArchivos] = useState({
    cedula: null,
    rif: null,
    certificacion_bancaria: null,
    recibos: []
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e, tipo) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande (máximo 15MB)');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos PNG, JPG o PDF');
      return;
    }

    setArchivos({
      ...archivos,
      [tipo]: file
    });
  };

  const handleRecibosChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 3) {
      toast.error('Máximo 3 recibos permitidos');
      return;
    }

    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error('Cada archivo debe ser menor a 15MB');
        return;
      }
    }

    setArchivos({
      ...archivos,
      recibos: files
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (formData.motivo_retiro.length < 50 || formData.motivo_retiro.length > 150) {
      toast.error('El motivo del retiro debe tener entre 50 y 150 caracteres');
      return;
    }

    if (!archivos.cedula || !archivos.rif || !archivos.certificacion_bancaria) {
      toast.error('Debes subir todos los documentos requeridos');
      return;
    }

    if (archivos.recibos.length === 0) {
      toast.error('Debes subir al menos un recibo de pago');
      return;
    }

    setLoading(true);

    try {
      console.log('🔧 Iniciando registro VIP...');
      console.log('🔧 Backend URL:', BACKEND_URL);
      
      toast.info('Procesando registro...');
      
      // Crear FormData con todos los archivos y datos
      const formDataToSend = new FormData();
      
      // Agregar datos del formulario
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Agregar archivos
      formDataToSend.append('cedula_file', archivos.cedula);
      formDataToSend.append('rif_file', archivos.rif);
      formDataToSend.append('certificacion_bancaria_file', archivos.certificacion_bancaria);
      
      // Agregar recibos
      archivos.recibos.forEach((recibo, index) => {
        formDataToSend.append(`recibo_${index}`, recibo);
      });

      console.log('🔧 Enviando petición al backend...');
      
      // FIX: Usar XMLHttpRequest para evitar conflicto con rrweb-recorder
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/registro-vip`, true);
        
        xhr.onload = function() {
          console.log('🔧 Respuesta recibida:', xhr.status, xhr.statusText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve({ ok: true, data: JSON.parse(xhr.responseText) });
            } catch (e) {
              reject(new Error('Error al procesar respuesta'));
            }
          } else {
            try {
              resolve({ ok: false, status: xhr.status, data: JSON.parse(xhr.responseText) });
            } catch (e) {
              resolve({ ok: false, status: xhr.status, data: { detail: xhr.responseText.substring(0, 100) } });
            }
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Error de conexión. Verifica tu internet.'));
        };
        
        xhr.send(formDataToSend);
      });
      
      if (result.ok) {
        console.log('✅ Registro exitoso:', result.data);
        setExpedienteNumero(result.data.expediente_numero);
        setExitoso(true);
        toast.success('¡Registro VIP completado exitosamente!');
      } else {
        console.error('Error del servidor:', result.data);
        const errorMsg = parseApiError(result.data.detail, 'Error al registrar');
        toast.error(errorMsg);
        logErrorToBackend({ tipo: 'registro_vip_error', endpoint: '/api/registro-vip', error: errorMsg, usuario: { cedula: formData.cedula, email: formData.email } });
      }
    } catch (error) {
      console.error('Error de conexion:', error);
      toast.error(`Error de conexion: ${error.message}. Por favor verifica tu conexion a internet.`);
      logErrorToBackend({ tipo: 'registro_vip_conexion_error', endpoint: '/api/registro-vip', error: error.message, usuario: { cedula: formData.cedula, email: formData.email } });
    } finally {
      setLoading(false);
    }
  };

  if (exitoso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          </div>

          <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-12 h-12 text-yellow-500" />
                </div>
              </div>

              <h1 className="text-3xl font-bold text-white mb-3">
                🌟 ¡Bienvenido Cliente VIP!
              </h1>
              
              <p className="text-gray-400 text-lg mb-4">
                Tu registro ha sido procesado exitosamente
              </p>

              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-5 mb-8">
                <p className="text-yellow-300 text-sm mb-3">
                  <strong>Tu número de expediente:</strong>
                </p>
                <p className="text-2xl font-bold text-yellow-500">
                  {expedienteNumero}
                </p>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-5 mb-6 text-left">
                <p className="text-blue-300 text-sm mb-2">
                  <strong>Próximos pasos:</strong>
                </p>
                <ul className="text-gray-300 text-sm space-y-2 list-disc list-inside">
                  <li>Nuestro equipo revisará tu solicitud en breve</li>
                  <li>Recibirás un email de confirmación</li>
                  <li>Un gerente se pondrá en contacto contigo</li>
                  <li>Tu proceso será priorizado como cliente VIP</li>
                </ul>
              </div>

              <p className="text-gray-500 text-sm">
                ¿Necesitas ayuda? Escríbenos a{' '}
                <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">
                  desactivate@kompa.global
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">🌟 Registro Cliente VIP</h1>
          <p className="text-gray-400">Formulario de registro acelerado para clientes VIP</p>
          <p className="text-xs text-gray-600 mt-2">v2.1.0 - Backend Managed</p>
        </div>

        {/* Indicador de progreso */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className={`text-sm ${step >= 1 ? 'text-yellow-500' : 'text-gray-500'}`}>
              1. Datos Personales
            </span>
            <span className={`text-sm ${step >= 2 ? 'text-yellow-500' : 'text-gray-500'}`}>
              2. Datos del Plan
            </span>
            <span className={`text-sm ${step >= 3 ? 'text-yellow-500' : 'text-gray-500'}`}>
              3. Documentos
            </span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full">
            <div
              className="h-full bg-yellow-500 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Paso 1: Datos Personales */}
          {step === 1 && (
            <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Datos Personales</CardTitle>
                <CardDescription className="text-gray-400">
                  Ingresa tu información de contacto
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Nombre Completo *</Label>
                    <Input
                      name="nombre_completo"
                      value={formData.nombre_completo}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Cédula *</Label>
                    <Input
                      name="cedula"
                      value={formData.cedula}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="V-12345678"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Teléfono *</Label>
                    <Input
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="+58 412-1234567"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Dirección *</Label>
                  <Textarea
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    required
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Siguiente →
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Paso 2: Datos del Plan */}
          {step === 2 && (
            <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Datos del Plan</CardTitle>
                <CardDescription className="text-gray-400">
                  Información sobre tu plan de retiro
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Fecha de Inscripción *</Label>
                    <Input
                      name="fecha_inscripcion"
                      type="date"
                      value={formData.fecha_inscripcion}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Fecha de Último Pago *</Label>
                    <Input
                      name="fecha_ultimo_pago"
                      type="date"
                      value={formData.fecha_ultimo_pago}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Monto Acumulado *</Label>
                    <Input
                      name="monto_acumulado"
                      value={formData.monto_acumulado}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="$10,000"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Plan Adquirido *</Label>
                    <Input
                      name="plan_adquirido"
                      value={formData.plan_adquirido}
                      onChange={handleChange}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Plan Premium"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Motivo del Retiro (50-150 caracteres) *</Label>
                  <Textarea
                    name="motivo_retiro"
                    value={formData.motivo_retiro}
                    onChange={handleChange}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={4}
                    minLength={50}
                    maxLength={150}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.motivo_retiro.length}/150 caracteres
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1 border-gray-600 text-gray-300"
                  >
                    ← Anterior
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(3)}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    Siguiente →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Paso 3: Documentos */}
          {step === 3 && (
            <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Documentos</CardTitle>
                <CardDescription className="text-gray-400">
                  Sube todos los documentos requeridos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cédula */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Cédula de Identidad *</Label>
                  {!archivos.cedula ? (
                    <div className="space-y-3">
                      {/* Inputs ocultos */}
                      <input
                        type="file"
                        id="cedula-camera"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileChange(e, 'cedula')}
                      />
                      <input
                        type="file"
                        id="cedula-gallery"
                        className="hidden"
                        accept="image/*,.pdf,.heic,.heif"
                        onChange={(e) => handleFileChange(e, 'cedula')}
                      />
                      
                      {/* Botones de selección */}
                      <div className="grid grid-cols-2 gap-3">
                        <label 
                          htmlFor="cedula-camera" 
                          className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <Camera className="w-8 h-8 text-yellow-500" />
                            <p className="text-gray-300 font-medium text-sm">Tomar Foto</p>
                            <p className="text-xs text-gray-500">Usar cámara</p>
                          </div>
                        </label>
                        
                        <label 
                          htmlFor="cedula-gallery" 
                          className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <FolderOpen className="w-8 h-8 text-blue-500" />
                            <p className="text-gray-300 font-medium text-sm">Elegir Archivo</p>
                            <p className="text-xs text-gray-500">Galería/PDF</p>
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-500" />
                        <p className="text-gray-300 font-medium">{archivos.cedula.name}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchivos({...archivos, cedula: null})}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>

                {/* RIF */}
                <div>
                  <Label className="text-gray-300 mb-2 block">RIF *</Label>
                  {!archivos.rif ? (
                    <div className="space-y-3">
                      <input
                        type="file"
                        id="rif-camera"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileChange(e, 'rif')}
                      />
                      <input
                        type="file"
                        id="rif-gallery"
                        className="hidden"
                        accept="image/*,.pdf,.heic,.heif"
                        onChange={(e) => handleFileChange(e, 'rif')}
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <label htmlFor="rif-camera" className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <Camera className="w-8 h-8 text-yellow-500" />
                            <p className="text-gray-300 font-medium text-sm">Tomar Foto</p>
                            <p className="text-xs text-gray-500">Usar cámara</p>
                          </div>
                        </label>
                        <label htmlFor="rif-gallery" className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <FolderOpen className="w-8 h-8 text-blue-500" />
                            <p className="text-gray-300 font-medium text-sm">Elegir Archivo</p>
                            <p className="text-xs text-gray-500">Galería/PDF</p>
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-500" />
                        <p className="text-gray-300 font-medium">{archivos.rif.name}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchivos({...archivos, rif: null})}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>

                {/* Certificación Bancaria */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Certificación Bancaria *</Label>
                  {!archivos.certificacion_bancaria ? (
                    <div className="space-y-3">
                      <input
                        type="file"
                        id="cert-camera"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileChange(e, 'certificacion_bancaria')}
                      />
                      <input
                        type="file"
                        id="cert-gallery"
                        className="hidden"
                        accept="image/*,.pdf,.heic,.heif"
                        onChange={(e) => handleFileChange(e, 'certificacion_bancaria')}
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <label htmlFor="cert-camera" className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <Camera className="w-8 h-8 text-yellow-500" />
                            <p className="text-gray-300 font-medium text-sm">Tomar Foto</p>
                            <p className="text-xs text-gray-500">Usar cámara</p>
                          </div>
                        </label>
                        <label htmlFor="cert-gallery" className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <FolderOpen className="w-8 h-8 text-blue-500" />
                            <p className="text-gray-300 font-medium text-sm">Elegir Archivo</p>
                            <p className="text-xs text-gray-500">Galería/PDF</p>
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-500" />
                        <p className="text-gray-300 font-medium">{archivos.certificacion_bancaria.name}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchivos({...archivos, certificacion_bancaria: null})}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>

                {/* Recibos de Pago */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Recibos de Pago (1-3 archivos) *</Label>
                  {archivos.recibos.length === 0 ? (
                    <div className="space-y-3">
                      <input
                        type="file"
                        id="recibos-camera"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={handleRecibosChange}
                      />
                      <input
                        type="file"
                        id="recibos-gallery"
                        className="hidden"
                        accept="image/*,.pdf,.heic,.heif"
                        multiple
                        onChange={handleRecibosChange}
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <label htmlFor="recibos-camera" className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <Camera className="w-8 h-8 text-yellow-500" />
                            <p className="text-gray-300 font-medium text-sm">Tomar Fotos</p>
                            <p className="text-xs text-gray-500">Cámara (múltiple)</p>
                          </div>
                        </label>
                        <label htmlFor="recibos-gallery" className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center gap-2">
                            <FolderOpen className="w-8 h-8 text-blue-500" />
                            <p className="text-gray-300 font-medium text-sm">Elegir Archivos</p>
                            <p className="text-xs text-gray-500">Galería/PDF</p>
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB c/u (1-3 archivos)</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {archivos.recibos.map((recibo, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-green-500" />
                            <p className="text-gray-300 text-sm">{recibo.name}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setArchivos({...archivos, recibos: archivos.recibos.filter((_, i) => i !== index)})}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            Eliminar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Alert */}
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-blue-300 text-sm">
                        <strong>Nota importante:</strong>
                      </p>
                      <ul className="text-gray-400 text-xs mt-2 space-y-1 list-disc list-inside">
                        <li>Todos los archivos deben ser claros y legibles</li>
                        <li>Formatos permitidos: PNG, JPG, PDF</li>
                        <li>Tamaño máximo por archivo: 15MB</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="flex-1 border-gray-600 text-gray-300"
                    disabled={loading}
                  >
                    ← Anterior
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Registrando...
                      </>
                    ) : (
                      <>
                        🌟 Completar Registro VIP
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          ¿Necesitas ayuda? Escríbenos a{' '}
          <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">
            desactivate@kompa.global
          </a>
        </p>
      </div>
    </div>
  );
}
